import { useState, useEffect } from "react";
import { AlertTriangle, Loader2, X, CreditCard } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";

export default function NoShowModal({ bookingId, onClose, onDone }) {
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/pos/appointments/${bookingId}/no-show-quote`).then((r) => setQ(r.data)).catch(() => setQ({ amount: 0, has_card: false }));
  }, [bookingId]);

  const chargeAndMark = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pos/appointments/${bookingId}/no-show-charge`);
      if (data.charged) toast.success(`No-show fee ${money(Number(data.amount))} charged`);
      else if (data.fee_status === "owed") toast.warning(`Marked no-show — ${money(Number(data.amount))} fee owed (${data.reason || "not charged"})`);
      else toast.success("Marked no-show");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const markOnly = async () => {
    setBusy(true);
    try {
      await api.put(`/pos/appointments/${bookingId}/status`, { status: "no_show" });
      toast.success("Marked no-show (no fee)");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const amount = Number(q?.amount || 0);
  return (
    <div className="fixed inset-0 z-[120] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="noshow-modal">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><AlertTriangle size={20} className="text-[#B5794E]" /> Mark no-show</h3>
          <button onClick={onClose} data-testid="noshow-cancel" className="text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        </div>
        {!q ? (
          <div className="py-8 text-center text-[#4A5D6B]"><Loader2 className="animate-spin inline" /></div>
        ) : (
          <>
            <p className="text-sm text-[#4A5D6B] mb-4">{q.member ? `${q.member} — ` : ""}{q.service}</p>
            {amount > 0 ? (
              <div className="eyw-card p-4 mb-4 text-sm">
                <p className="text-[#1A242B]"><b>Fee: {money(amount)}</b> <span className="text-[#8FA89B]">({q.basis === "late_fee" ? "center late fee" : "deposit forfeit"})</span></p>
                <p className={`text-xs mt-1 ${q.has_card ? "text-[#5C8A72]" : "text-[#B5794E]"}`}>{q.has_card ? "Will charge the member's card on file." : "No card on file — will be flagged as fee owed."}</p>
              </div>
            ) : <p className="text-sm text-[#8FA89B] mb-4">No fee is set for this booking.</p>}
            <div className="space-y-2">
              {amount > 0 && (
                <button onClick={chargeAndMark} disabled={busy} data-testid="noshow-charge" className="eyw-btn w-full justify-center disabled:opacity-60">
                  {busy ? <Loader2 className="animate-spin" size={17} /> : <CreditCard size={16} />} {q.has_card ? `Charge ${money(amount)} & mark no-show` : `Flag ${money(amount)} owed & mark no-show`}
                </button>
              )}
              <button onClick={markOnly} disabled={busy} data-testid="noshow-mark-only" className="w-full py-2.5 rounded-full border border-[#E5E0D8] text-[#4A5D6B] text-sm hover:bg-[#F1EEE8]">Mark no-show without charging</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
